import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";

import { normalizeCountryCode } from "~/features/quote/utils/quote-location-utils.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlags } from "~/shared/hooks/use-feature-flags.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	AddressBookCreateBodyV2,
	AddressBookCreateResponseV2,
	AddressBookFilteredQueryParams,
	AddressBookItemSummary,
	AddressBookQueryParams,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import {
	addressBookCreateResponseV2Schema,
	addressBookItemSummarySchema,
	addressBookUpdateResponseV2Schema,
} from "~/shared/schemas/address-book-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import { zipCodeSchema } from "~/shared/schemas/location-state-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	getAndValidate,
	postAndValidate,
	putAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import { formatTimeToString } from "~/shared/utils/time-utils.ts";

const deleteResponseSchema = z.union([
	z.void(),
	z.null(),
	z.string(),
	z.object({}).passthrough(),
]);

/**
 * Converts an AddressBookItemSummary to AddressBookUpdateBodyV2
 * Used when updating an address book item from summary data
 */
export const convertSummaryToUpdateBody = (
	summary: AddressBookItemSummary,
): AddressBookUpdateBodyV2 => {
	return {
		addressBookItemId: summary.addressBookItemId,
		customerId: summary.customerId,
		companyName: summary.companyName ?? undefined,
		address1: summary.address1,
		address2: summary.address2 ?? undefined,
		postalCode: summary.postalCode ?? undefined,
		city: summary.city ?? undefined,
		stateProvince: summary.stateProvince ?? undefined,
		country: summary.country,
		isValidated: summary.isValidated ?? undefined,
		legacyAddressId: summary.legacyAddressId ?? undefined,
		details: summary.details.map((detail) => ({
			contactDetailId: detail.contactDetailId,
			addressBookItemId: detail.addressBookItemId,
			contactName: detail.contactName ?? undefined,
			contactPhone: detail.contactPhone ?? undefined,
			contactExtension: detail.contactExtension,
			hoursOpen: detail.hoursOpen ?? undefined,
			hoursClose: detail.hoursClose ?? undefined,
			stopType: detail.stopType,
			mode: detail.mode,
			timeZone: detail.timeZone ?? undefined,
			specialInstructions: detail.specialInstructions ?? undefined,
			accessorials: detail.accessorials ?? undefined,
		})),
	};
};

// Format phone number: "(789) 789-7898" -> "7897897898"
const formatPhoneNumber = (phone: string | undefined): string => {
	if (!phone) return "";
	return phone.replaceAll(/\D/g, "");
};

// Format address book body details before API call
const formatAddressBookBody = <
	T extends AddressBookCreateBodyV2 | AddressBookUpdateBodyV2,
>(
	body: T,
): T => {
	return {
		...body,
		country: normalizeCountryCode(body.country),
		details: body.details?.map((detail) => ({
			...detail,
			contactPhone: formatPhoneNumber(detail.contactPhone),
			hoursOpen: formatTimeToString(detail.hoursOpen),
			hoursClose: formatTimeToString(detail.hoursClose),
		})),
	};
};

type UseAddressBookReturn = {
	createAddressBookItem: (
		body: AddressBookCreateBodyV2,
	) => Promise<AddressBookCreateResponseV2>;
	isCreating: boolean;
	updateAddressBookItem: (
		body: AddressBookUpdateBodyV2,
		options?: { successMessage?: string; errorMessage?: string },
	) => Promise<void>;
	isUpdating: boolean;
	deleteAddressBookItem: (addressBookItemId: number) => Promise<void>;
	isDeleting: boolean;
};

export const useAddressBook = (): UseAddressBookReturn => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();
	const { isInternal } = useDeploymentType();

	const createMutation = useMutation({
		mutationFn: async (body: AddressBookCreateBodyV2) => {
			const formattedBody = formatAddressBookBody(body);
			const route = isInternal
				? apiRoutes.CREATEADDRESSBOOKITEMV2
				: apiRoutes.EXTERNALCREATEADDRESSBOOKITEMV2;
			const result = await postAndValidate(
				route,
				formattedBody,
				addressBookCreateResponseV2Schema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["addressBookItems"],
			});
			queryClient.invalidateQueries({
				queryKey: ["addressBookItemsFiltered"],
			});
			showSuccess("Address Book Item saved successfully.");
			queryClient.invalidateQueries({ queryKey: ["addressBookSearch"] });
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				error.message ||
					"Failed to create Address Book Item. Please try again later.",
			);
		},
	});

	const { mutateAsync: createMutateAsync } = createMutation;
	const createAddressBookItem = useCallback(
		async (
			body: AddressBookCreateBodyV2,
		): Promise<AddressBookCreateResponseV2> => {
			return await createMutateAsync(body);
		},
		[createMutateAsync],
	);

	const updateMutation = useMutation({
		mutationFn: async ({
			body,
		}: {
			body: AddressBookUpdateBodyV2;
			successMessage?: string;
			errorMessage?: string;
		}) => {
			const formattedBody = formatAddressBookBody(body);
			const baseRoute = isInternal
				? apiRoutes.UPDATEADDRESSBOOKITEMV2
				: apiRoutes.EXTERNALUPDATEADDRESSBOOKITEMV2;
			const route = baseRoute.replace(
				":addressBookItemId",
				String(body.addressBookItemId),
			);
			const result = await putAndValidate(
				route,
				formattedBody,
				addressBookUpdateResponseV2Schema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result;
		},
		onSuccess: (_data, variables) => {
			showSuccess(variables.successMessage ?? "Address updated successfully.");
			queryClient.invalidateQueries({ queryKey: ["addressBookSearch"] });
			queryClient.invalidateQueries({
				queryKey: ["addressBookItems"],
			});
			queryClient.invalidateQueries({
				queryKey: ["addressBookItemsFiltered"],
			});
			queryClient.invalidateQueries({
				queryKey: ["addressBookHistory", variables.body.addressBookItemId],
			});
		},
		onError: (error: Error, variables) => {
			console.error(error);
			showError(
				variables.errorMessage ??
					"Failed to update Address Book Item. Please try again later.",
			);
		},
	});

	const { mutateAsync: updateMutateAsync } = updateMutation;
	const updateAddressBookItem = useCallback(
		async (
			body: AddressBookUpdateBodyV2,
			options?: { successMessage?: string; errorMessage?: string },
		): Promise<void> => {
			await updateMutateAsync({ body, ...options });
		},
		[updateMutateAsync],
	);

	const deleteMutation = useMutation({
		mutationFn: async (addressBookItemId: number) => {
			const baseRoute = isInternal
				? apiRoutes.DELETEADDRESSBOOKITEMV2
				: apiRoutes.EXTERNALDELETEADDRESSBOOKITEMV2;
			const route = baseRoute.replace(
				":addressBookItemId",
				String(addressBookItemId),
			);
			const result = await deleteAndValidate(route, deleteResponseSchema);

			if (!result.success) {
				throw new Error(result.error);
			}

			return { addressBookItemId };
		},
		onMutate: async (addressBookItemId: number) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["addressBookItems"] });

			// Snapshot the previous value for all matching queries
			const previousData = queryClient.getQueriesData<AddressBookItemSummary[]>(
				{ queryKey: ["addressBookItems"] },
			);

			// Optimistically remove the item from all matching queries
			queryClient.setQueriesData<AddressBookItemSummary[]>(
				{ queryKey: ["addressBookItems"] },
				(old) =>
					old?.filter((item) => item.addressBookItemId !== addressBookItemId),
			);

			return { previousData };
		},
		onSuccess: () => {
			showSuccess("Address deleted successfully.");
		},
		onError: (
			error,
			_addressBookItemId,
			context?: {
				previousData: [unknown, AddressBookItemSummary[] | undefined][];
			},
		) => {
			// Rollback to the previous value on error
			if (context?.previousData) {
				for (const [queryKey, data] of context.previousData) {
					queryClient.setQueryData(queryKey as unknown[], data);
				}
			}
			console.error(error);
			showError("Failed to delete Address Book Item. Please try again later.");
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["addressBookSearch"] });
			// Always refetch after error or success to ensure cache is in sync
			queryClient.invalidateQueries({
				queryKey: ["addressBookItems"],
			});
			queryClient.invalidateQueries({
				queryKey: ["addressBookItemsFiltered"],
			});
		},
	});

	const { mutateAsync: deleteMutateAsync } = deleteMutation;
	const deleteAddressBookItem = useCallback(
		async (addressBookItemId: number): Promise<void> => {
			await deleteMutateAsync(addressBookItemId);
		},
		[deleteMutateAsync],
	);

	return {
		createAddressBookItem,
		isCreating: createMutation.isPending,
		updateAddressBookItem,
		isUpdating: updateMutation.isPending,
		deleteAddressBookItem,
		isDeleting: deleteMutation.isPending,
	};
};

/**
 * Hook to fetch address book items filtered by location and stop type
 * Used on the Shipment Details screen to show only addresses matching the selected location
 */
export const useAddressBookItemsFiltered = (
	params: AddressBookFilteredQueryParams | null,
	enabled = true,
	isInternal = false,
) => {
	const isValidParams =
		params !== null &&
		Boolean(params.customerId) &&
		params.customerId > 0 &&
		Boolean(params.postalCode) &&
		Boolean(params.city) &&
		Boolean(params.state) &&
		Boolean(params.country);

	return useQuery<AddressBookItemSummary[]>({
		queryKey: ["addressBookItemsFiltered", params],
		queryFn: async (): Promise<AddressBookItemSummary[]> => {
			if (!params) return [];

			const queryParams = new URLSearchParams({
				customerId: String(params.customerId),
				postalCode: params.postalCode,
				city: params.city,
				state: params.state,
				country: params.country,
				stopType: params.stopType,
			});

			const url = isInternal
				? `${apiRoutes.ADDRESSBOOKSUMMARIES}?${queryParams.toString()}`
				: `${apiRoutes.EXTERNALADDRESSBOOKSUMMARIES}?${queryParams.toString()}`;

			const result = await getAndValidate(
				url,
				addressBookItemSummarySchema.array(),
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		enabled: enabled && isValidParams,
		retry: 3,
		staleTime: 0,
		gcTime: 1000 * 60 * 10, // 10 minutes
		refetchOnMount: "always", // Always refetch when modal opens
		refetchOnWindowFocus: false,
	});
};

export const useAddressBookItems = (
	params: AddressBookQueryParams,
	enabled = true,
	isInternal = false,
) => {
	return useQuery<AddressBookItemSummary[]>({
		queryKey: ["addressBookItems", params],
		queryFn: async (): Promise<AddressBookItemSummary[]> => {
			const url = isInternal
				? apiRoutes.ADDRESSBOOK.replace(
						":customerId",
						String(params.customerId),
					)
				: apiRoutes.EXTERNALADDRESSBOOK;

			const result = await getAndValidate(
				url,
				addressBookItemSummarySchema.array(),
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		enabled: enabled && Boolean(params.customerId) && params.customerId > 0,
		retry: 3,
		staleTime: 0,
		gcTime: 1000 * 60 * 10, // 10 minutes
		refetchOnMount: "always", // Always refetch when modal opens
		refetchOnWindowFocus: false,
	});
};

export const searchAddressBookItems = async ({
	searchTerm,
	customerId,
	isInternal,
}: {
	searchTerm: string;
	customerId?: string | number;
	isInternal: boolean;
}): Promise<AddressBookItemSummary[]> => {
	const url = isInternal
		? apiRoutes.ADDRESSBOOKSEARCH.replace(
				":searchTerm",
				encodeURIComponent(searchTerm),
			).replace(":customerId", customerId?.toString() ?? "")
		: apiRoutes.EXTERNALADDRESSBOOKSEARCH.replace(
				":searchTerm",
				encodeURIComponent(searchTerm),
			);

	const result = await getAndValidate(
		url,
		addressBookItemSummarySchema.array(),
	);

	if (!result.success) {
		throw new Error(result.error);
	}

	return result.data;
};

export const MIN_ADDRESS_BOOK_SEARCH_LENGTH = 3;

export const useAddressBookSearch = (
	searchTerm: string,
	customerId?: string | number,
	enabled = true,
	isInternal = false,
) => {
	const isValidSearchTerm =
		searchTerm.trim().length >= MIN_ADDRESS_BOOK_SEARCH_LENGTH;

	return useQuery<AddressBookItemSummary[]>({
		queryKey: ["addressBookSearch", searchTerm, customerId, isInternal],
		queryFn: async (): Promise<AddressBookItemSummary[]> =>
			searchAddressBookItems({ searchTerm, customerId, isInternal }),
		enabled: enabled && isValidSearchTerm && Boolean(customerId),
		retry: 3,
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 10, // 10 minutes
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
};
export const usePostalCodeSearch = (searchTerm: string, enabled = true) => {
	const { flags } = useFeatureFlags();
	const isInternationalQuotingEnabled = flags["traxLtlInternational"] === true;

	const isValidSearchTerm =
		searchTerm.trim().length > 0 && /^[a-zA-Z0-9\s,-]+$/.test(searchTerm);

	return useQuery<ZipCode[]>({
		queryKey: ["postalCodeSearch", searchTerm, isInternationalQuotingEnabled],
		queryFn: async (): Promise<ZipCode[]> => {
			const countries = isInternationalQuotingEnabled
				? ["USA", "CAN", "MEX"]
				: ["USA"];

			const params = new URLSearchParams();
			params.set("searchString", searchTerm);
			for (const country of countries) {
				params.append("countries", country);
			}

			const url = `${apiRoutes.ZIPCODESELECT}?${params.toString()}`;
			const result = await getAndValidate(url, zipCodeSchema.array());

			if (!result.success) {
				return [];
			}

			return result.data;
		},
		enabled: enabled && isValidSearchTerm,
		retry: 3,
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 10, // 10 minutes
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
};
